import React, { useState, useEffect, useContext, useRef } from 'react';
import * as XLSX from 'xlsx';
import { ModeloRetribucionOptions } from '../../types/DropdownMapping';
import getPayfitFileData, { PayfitUserData } from '../../api/thirdparty/payfit/getPayfitFileData';
import getCompaniesPayfitStatus, {
	PayfitCompanyStatus
} from '../../api/thirdparty/payfit/getCompaniesPayfitStatus';
import { fillPayfitRowData, generatePayfitRow } from '../../utils/payfitFileGenerator';
import { PAYFIT_FILE_HEADERS } from '../../constants/PayfitConstants';
import listCollaborators, {
	PayfitCollaborator
} from '../../api/thirdparty/payfit/listCollaborators';
import { ArcaContext } from '../../context/ArcaContext.context';
import { formatArcaNumber } from '../../utils/formatArcaNumbers';
import ProjectConfig from '../../constants';
import { SoftwareHHRR } from './softwareHHRR';
import { analytics } from '../../services/analytics';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import getActualToken, { GetActualTokenResponse } from '../../api/thirdparty/payfit/getActualToken';
import { getPayfitAuthUrl } from '../../constants/PayfitConstants';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { X, RotateCw, Loader2, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { esApp } from '../../movil/plataforma';
import { toast } from '../ui/sonner';

interface PayfitFileModalV2Props {
	isOpen: boolean;
	onClose: () => void;
}

const PayfitFileModalV2: React.FC<PayfitFileModalV2Props> = ({ isOpen, onClose }) => {
	const { token, companyId } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [sheetData, setSheetData] = useState<any[][] | null>(null);
	const [dataVisible, setDataVisible] = useState(false);
	const [fileName, setFileName] = useState<string | null>(null);
	const [magicFilled, setMagicFilled] = useState<boolean>(false);
	const [payfitFileData, setPayfitFileData] = useState<PayfitUserData[]>();
	const [selectedSoftware, setSelectedSoftware] = useState<SoftwareHHRR | null>(null);
	const [otherSoftware, setOtherSoftware] = useState<string>('');
	const [accessToken, setAccessToken] = useState<string>('');
	const [isPayfitConnected, setIsPayfitConnected] = useState(false);
	// En la app, conectar PayFit no puede terminar: PayFit devuelve al usuario a la dirección de la
	// que salió, que dentro de la app es el `localhost` del WebView. Se hace desde el ordenador: el
	// botón se ve igual y avisa al pulsarlo (un texto en su lugar pasaba desapercibido, war room 19-oct).
	const conectarSoloEnOrdenador = esApp();
	const payfitConnectedRef = useRef(false);
	const [actualToken, setActualToken] = useState<GetActualTokenResponse | null>(null);
	const [templateFileData, setTemplateFileData] = useState<any[]>([]);
	const [templateFileVisible, setTemplateFileVisible] = useState(false);
	const [companiesStatus, setCompaniesStatus] = useState<PayfitCompanyStatus[]>([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
	const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
	const popupRef = useRef<Window | null>(null);

	const handleCloseModal = (shouldCloseModal = true) => {
		setSheetData(null);
		setDataVisible(false);
		setFileName(null);
		setMagicFilled(false);
		setOtherSoftware('');
		setAccessToken('');
		setSelectedSoftware(null);
		setCompaniesStatus([]);
		setSelectedCompanyId(null);
		setPayfitFileData(undefined);

		if (popupRef.current && !popupRef.current.closed) {
			popupRef.current.close();
			popupRef.current = null;
		}

		if (shouldCloseModal) onClose();
	};

	const handleInformArca = () => {
		analytics.track('hhrr_software_not_available', { software_name: otherSoftware });
		handleCloseModal();
	};

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		analytics.track('payfit_funnel', { step: 'file_upload' });
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (evt) => {
			const bstr = evt.target?.result;
			let data = XLSX.utils.sheet_to_json(
				XLSX.read(bstr, { type: 'binary' }).Sheets[
					XLSX.read(bstr, { type: 'binary' }).SheetNames[0]
				],
				{ header: 1 }
			) as any[][];

			data = data.slice(0, data.length - 99);

			setFileName(file.name);

			const nonEmptyRows = data.filter((row) =>
				row.some((cell) => cell !== undefined && cell !== null && String(cell).trim() !== '')
			);
			if (nonEmptyRows.length === 0) {
				setSheetData(null);
				return;
			}

			const maxColumns = nonEmptyRows.reduce((max, row) => Math.max(max, row.length), 0);
			const columnsToKeep: number[] = [];
			for (let col = 0; col < maxColumns; col++) {
				const hasContent = nonEmptyRows.some((row) => {
					const cell = row[col];
					return cell !== undefined && cell !== null && String(cell).trim() !== '';
				});
				if (hasContent) columnsToKeep.push(col);
			}

			const filteredData = nonEmptyRows.map((row) => columnsToKeep.map((colIdx) => row[colIdx]));
			setSheetData(filteredData);
		};
		reader.readAsArrayBuffer(file);
	};

	const handleAutoFill = () => {
		analytics.track('payfit_funnel', { step: 'file_autofill' });
		if (!sheetData || !payfitFileData) return;

		const newSheetData = sheetData.map((row, index) => {
			if (index === 0) return row;
			const filledRow = fillPayfitRowData(row, payfitFileData);
			return filledRow || row;
		});
		setSheetData(newSheetData);
		setMagicFilled(true);
	};

	const handleDownload = () => {
		if (!sheetData) return;
		const ws = XLSX.utils.aoa_to_sheet(sheetData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		const downloadName = fileName
			? `magico_${fileName}`
			: translate('payfit.fileProcessing.magicFileName');
		XLSX.writeFile(wb, downloadName);
	};

	const handleVisualize = () => {
		setDataVisible(!dataVisible);
	};

	useEffect(() => {
		if (token && selectedCompanyId) {
			const fetchPayfitFileData = async () => {
				try {
					const response = await getPayfitFileData(token, selectedCompanyId);
					setPayfitFileData(response?.data);
				} catch (error) {
					console.error(translate('payfit.errors.getPayfitData'), error);
				}
			};

			fetchPayfitFileData();
		}
	}, [token, selectedCompanyId]);

	useEffect(() => {
		if (token && isOpen && companiesStatus.length === 0) {
			const fetchCompaniesStatus = async () => {
				try {
					const response = await getCompaniesPayfitStatus(token);
					const companies = response?.data?.companies || [];
					setCompaniesStatus(companies);

					const connectedCompanies = companies.filter((c) => c.hasPayfitToken);
					if (connectedCompanies.length === 1) {
						setSelectedCompanyId(connectedCompanies[0].companyId);
					}
				} catch (error) {
					console.error('Error fetching companies Payfit status:', error);
				}
			};

			fetchCompaniesStatus();
		}
	}, [token, isOpen, companiesStatus.length]);

	const handleConnectPayfit = (companyId?: string) => {
		const csrf = crypto.randomUUID();
		sessionStorage.setItem('payfit_state', csrf);

		if (companyId) {
			sessionStorage.setItem('payfit_company_id', companyId);
		}

		const url = new URL(getPayfitAuthUrl());
		url.searchParams.set('state', csrf);

		const popup = window.open(
			url.toString(),
			'payfit_auth',
			'width=600,height=750,scrollbars=yes,resizable=yes'
		);
		popupRef.current = popup;

		if (!popup) {
			console.error(translate('payfit.popup.blocked'));
			window.location.href = url.toString();
			return;
		}

		const popupCloseInterval = setInterval(() => {
			if (popup.closed) {
				clearInterval(popupCloseInterval);

				setTimeout(() => {
					if (token) {
						getCompaniesPayfitStatus(token)
							.then((response) => {
								const companies = response?.data?.companies || [];
								setCompaniesStatus([...companies]);

								const connectedCompanies = companies.filter((c) => c.hasPayfitToken);
								if (connectedCompanies.length === 1) {
									setSelectedCompanyId(connectedCompanies[0].companyId);
								}
							})
							.catch((error) => {
								console.error('Error refreshing companies status after popup close:', error);
							});
					}
				}, 1000);
			}
		}, 500);
	};

	const avisarConectarEnOrdenador = () => {
		toast.info(translate('payfit.connection.soloOrdenador'));
	};

	// En la app, el botón «Conectar» no sale a PayFit: avisa. La web conecta igual que siempre.
	const conectarPayfit = conectarSoloEnOrdenador ? avisarConectarEnOrdenador : handleConnectPayfit;

	const handleRefreshCompaniesStatus = async () => {
		if (!token) return;

		setIsRefreshingStatus(true);

		try {
			const response = await getCompaniesPayfitStatus(token);
			const companies = response?.data?.companies || [];
			setCompaniesStatus([...companies]);

			const connectedCompanies = companies.filter((c) => c.hasPayfitToken);
			if (connectedCompanies.length === 1) {
				setSelectedCompanyId(connectedCompanies[0].companyId);
			}
		} catch (error) {
			console.error('Error during manual refresh:', error);
		} finally {
			setIsRefreshingStatus(false);
		}
	};

	useEffect(() => {
		const checkConnected = () => {
			const connected = !!localStorage.getItem('payfit_connected');
			setIsPayfitConnected(connected);
			payfitConnectedRef.current = connected;
		};

		checkConnected();

		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) {
				return;
			}

			if (event.data.type === 'PAYFIT_CONNECTION_SUCCESS') {
				setIsPayfitConnected(true);
				payfitConnectedRef.current = true;

				if (popupRef.current && !popupRef.current.closed) {
					popupRef.current.close();
					popupRef.current = null;
				}

				sessionStorage.removeItem('payfit_company_id');

				if (token) {
					getCompaniesPayfitStatus(token)
						.then((response) => {
							const companies = response?.data?.companies || [];
							setCompaniesStatus([...companies]);

							const connectedCompanies = companies.filter((c) => c.hasPayfitToken);
							if (connectedCompanies.length === 1) {
								setSelectedCompanyId(connectedCompanies[0].companyId);
							}
						})
						.catch((error) => {
							console.error('Error refreshing companies status:', error);
						});
				}
			} else if (event.data.type === 'PAYFIT_CONNECTION_ERROR') {
				console.error(translate('payfit.errors.connectionFailed'), event.data.error);
				setIsPayfitConnected(false);
				payfitConnectedRef.current = false;

				if (popupRef.current && !popupRef.current.closed) {
					popupRef.current.close();
					popupRef.current = null;
				}
			}
		};

		const onStorage = (ev: StorageEvent) => {
			if (ev.key === 'payfit_connected') {
				checkConnected();
			}
		};
	}, [token]);

	useEffect(() => {
		if (selectedSoftware === SoftwareHHRR.PAYFIT && selectedCompanyId) {
			getActualToken(token, selectedCompanyId)
				.then((data) => {
					setActualToken(data);
					if (data?.data !== 'PAYFIT_TOKEN_NOT_FOUND_FOR_COMPANY') {
						setIsPayfitConnected(true);
					} else {
						setIsPayfitConnected(false);
					}
				})
				.catch((error) => {
					console.error(translate('payfit.errors.getTokenAfterConnection'), error);
					setActualToken(null);
					setIsPayfitConnected(false);
				});
		}
	}, [selectedSoftware, token, selectedCompanyId]);

	useEffect(() => {
		if (actualToken && typeof actualToken.data === 'object') {
			setAccessToken(actualToken.data?.accessToken || '');
		}
	}, [actualToken]);

	const handleGenerateTemplateFile = async () => {
		try {
			const currentActualToken = await getActualToken(token, selectedCompanyId || undefined);

			if (!currentActualToken?.data || typeof currentActualToken.data !== 'object') {
				console.error(translate('payfit.errors.noTokenAvailable'));
				return;
			}

			const currentPayfitAccessToken = currentActualToken.data.accessToken;
			const currentPayfitCompanyId = currentActualToken.data.payfitCompanyId;

			if (!currentPayfitAccessToken || !currentPayfitCompanyId) {
				console.error(translate('payfit.errors.tokenOrCompanyIdNotAvailable'));
				return;
			}

			const collaboratorsRes = await listCollaborators(
				token,
				currentPayfitAccessToken,
				currentPayfitCompanyId
			);
			const collaborators = collaboratorsRes?.data?.collaborators || [];
			const activeCollaborators = collaborators.filter((collaborator: PayfitCollaborator) =>
				collaborator.contracts.some((contract) => contract.status === 'ACTIVE')
			);

			if (!payfitFileData) {
				console.error(translate('payfit.errors.noPayfitDataAvailable'));
				return;
			}

			const activeCollaboratorsWithPayfitData = activeCollaborators.map(
				(collaborator: PayfitCollaborator) => {
					const payfitData = payfitFileData.find((data) => {
						if (data.dni === collaborator.matricule) {
							return true;
						}

						const collaboratorEmails = collaborator.emails.map((email) => email.email);
						if (collaboratorEmails.includes(data.email)) {
							return true;
						}

						return false;
					});

					return {
						collaborator,
						payfitData
					};
				}
			);

			const finalTemplateData = [];

			finalTemplateData.push(PAYFIT_FILE_HEADERS);

			activeCollaboratorsWithPayfitData.forEach((item) => {
				const { payfitData, collaborator } = item;

				if (!payfitData) return;

				const importeMensualTotal = formatArcaNumber(
					Number(payfitData.nextParticipantContribution) +
						Number(payfitData.nextPromoterContribution)
				);

				const fullName = [
					collaborator.firstName,
					collaborator.lastName,
					collaborator.secondLastName
				]
					.filter(Boolean)
					.join(' ')
					.toUpperCase();

				const activeContracts = collaborator.contracts.filter(
					(contract) => contract.status === 'ACTIVE'
				);
				const lastActiveContract = activeContracts[activeContracts.length - 1];
				const contractId = lastActiveContract?.id || '';

				const modeloRetribucion =
					payfitData.nextParticipantContribution > 0
						? ModeloRetribucionOptions.ModeloMixto
						: ModeloRetribucionOptions.BeneficioEnEspecie;

				const newRow = generatePayfitRow(
					contractId,
					fullName,
					payfitData.dni || '',
					modeloRetribucion,
					importeMensualTotal,
					formatArcaNumber(Number(payfitData.nextPromoterContribution))
				);

				finalTemplateData.push(newRow);
			});

			setTemplateFileData(finalTemplateData);
		} catch (err) {
			console.error(translate('payfit.errors.generatingTemplateFile'), err);
		}
	};

	const handleVisualize2 = () => {
		setTemplateFileVisible((v) => !v);
	};

	const handleDownload2 = () => {
		if (!templateFileData) return;
		const ws = XLSX.utils.aoa_to_sheet(templateFileData);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		const downloadName = translate('payfit.fileProcessing.templateFileName');
		XLSX.writeFile(wb, downloadName);
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) handleCloseModal();
			}}
		>
			<DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{translate('payfit.fileModal.header')}</DialogTitle>
					<DialogDescription className="sr-only">
						{translate('payfit.fileModal.header')}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* Software Selector */}
					{!selectedSoftware && (
						<div className="mt-4">
							<Select
								onValueChange={(v) => {
									if (v !== SoftwareHHRR.SELECT) setSelectedSoftware(v as SoftwareHHRR);
								}}
							>
								<SelectTrigger className="w-full max-w-sm bg-primary! text-primary-foreground! border-none rounded-full font-medium px-6 py-2.5 hover:bg-primary/90!">
									<SelectValue placeholder={translate('software.selector.placeholder')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={SoftwareHHRR.PAYFIT}>
										<div className="flex items-center gap-2">
											<img src="/imgs/payfit.png" alt="" className="w-5 h-5" />
											Payfit
										</div>
									</SelectItem>
									<SelectItem value={SoftwareHHRR.OTHER}>
										{translate('software.other') || 'Otro'}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					)}

					{/* Payfit Flow */}
					{selectedSoftware === SoftwareHHRR.PAYFIT && (
						<>
							{/* Companies Payfit Connection Status */}
							{companiesStatus.length > 0 && (
								<div className="mt-4 mb-4 p-4 border border-border rounded-md">
									<div className="flex justify-between items-center mb-2">
										<p className="font-bold">Selecciona la compañía para generar el archivo</p>
										<Button
											variant="outline"
											size="sm"
											onClick={handleRefreshCompaniesStatus}
											disabled={isRefreshingStatus}
										>
											{isRefreshingStatus ? (
												<Loader2 className="w-4 h-4 animate-spin mr-1" />
											) : (
												<RotateCw className="w-4 h-4 mr-1" />
											)}
											{isRefreshingStatus ? 'Actualizando...' : 'Actualizar'}
										</Button>
									</div>

									{/* Connected companies */}
									{companiesStatus
										.filter((company) => company.hasPayfitToken)
										.map((company) => (
											<div
												key={company.companyId}
												className={cn(
													'flex justify-between items-center p-2 mb-2 border-2 rounded-md cursor-pointer transition-colors',
													selectedCompanyId === company.companyId
														? 'border-primary bg-primary/5'
														: 'border-border bg-background hover:bg-muted/50'
												)}
												tabIndex={0}
												role="button"
												aria-label={`${translate('payfit.fileModal.selectCompany')} ${company.companyName}`}
												aria-pressed={selectedCompanyId === company.companyId}
												onClick={() => setSelectedCompanyId(company.companyId)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														setSelectedCompanyId(company.companyId);
													}
												}}
											>
												<p
													className={
														selectedCompanyId === company.companyId ? 'font-bold' : 'font-normal'
													}
												>
													{company.companyName}
												</p>
												<Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
													{translate('payfit.connection.connected')}
												</Badge>
											</div>
										))}

									{/* Unconnected companies */}
									{companiesStatus.filter((company) => !company.hasPayfitToken).length > 0 && (
										<>
											<p className="font-bold mt-4 mb-2">Compañías sin conectar</p>
											{companiesStatus
												.filter((company) => !company.hasPayfitToken)
												.map((company) => (
													<div
														key={company.companyId}
														className="flex justify-between items-center p-2 mb-2 border border-border rounded-md"
													>
														<p>{company.companyName}</p>
														<Button
															size="sm"
															className="btn-arca-primary"
															onClick={() => conectarPayfit(company.companyId)}
														>
															{translate('payfit.connection.connect')}
														</Button>
													</div>
												))}
										</>
									)}
								</div>
							)}

							{/* Tabs */}
							{selectedCompanyId && (
								<Tabs defaultValue="automatic" className="mt-6">
									<TabsList>
										<TabsTrigger value="automatic">
											{translate('payfit.tabs.automaticConnection')}
										</TabsTrigger>
										<TabsTrigger value="manual">
											{translate('payfit.tabs.manualConnection')}
										</TabsTrigger>
									</TabsList>

									{/* Automatic Tab */}
									<TabsContent value="automatic">
										<Button
											className={cn(
												'mr-2',
												isPayfitConnected
													? 'bg-emerald-400 hover:bg-emerald-500 text-foreground'
													: 'btn-arca-primary'
											)}
											onClick={() => conectarPayfit()}
											disabled={isPayfitConnected}
										>
											{isPayfitConnected
												? translate('payfit.connection.connected')
												: translate('payfit.connection.connect')}
										</Button>
										<ol className="list-decimal pl-5 mt-2 space-y-1">
											<li>{translate('payfit.fileModal.automatic.step1')}</li>
											<li>{translate('payfit.fileModal.automatic.step2')}</li>
											<li>{translate('payfit.fileModal.automatic.step3')}</li>
											<li>{translate('payfit.fileModal.automatic.step4')}</li>
										</ol>
										{isPayfitConnected && (
											<>
												<div className="flex flex-wrap gap-2 mb-4 mt-4">
													<Button className="btn-arca-primary" onClick={handleGenerateTemplateFile}>
														{translate('payfit.fileProcessing.generateFileAutomatically')}
													</Button>
												</div>

												<div className="mt-2">
													<div className="flex flex-wrap gap-2">
														<Button className="btn-arca-primary" onClick={handleVisualize2}>
															{translate('payfit.fileProcessing.visualize')}
														</Button>
														{templateFileData && templateFileData.length > 0 && (
															<Button
																variant="outline"
																onClick={() => {
																	analytics.track('payfit_funnel', {
																		step: 'file_download'
																	});
																	handleDownload2();
																}}
															>
																{translate('payfit.fileProcessing.downloadMagicFile')}
															</Button>
														)}
													</div>
													{templateFileVisible &&
														templateFileData &&
														templateFileData.length > 0 && (
															<div className="mt-8 mb-16 overflow-auto max-h-[300px] p-2 border border-border">
																<p className="font-bold mb-2">
																	{translate('payfit.fileProcessing.templateFileGenerated')}
																</p>
																<Table>
																	<TableHeader>
																		<TableRow>
																			{templateFileData[0].map((cell: any, cellIndex: number) => (
																				<TableHead key={cellIndex}>{cell}</TableHead>
																			))}
																		</TableRow>
																	</TableHeader>
																	<TableBody>
																		{templateFileData
																			.slice(1)
																			.map((row: any[], rowIndex: number) => (
																				<TableRow
																					key={rowIndex}
																					className={rowIndex % 2 === 0 ? 'bg-muted/30' : ''}
																				>
																					{row.map((cell: any, cellIndex: number) => (
																						<TableCell key={cellIndex}>{cell}</TableCell>
																					))}
																				</TableRow>
																			))}
																	</TableBody>
																</Table>
															</div>
														)}
												</div>
											</>
										)}
									</TabsContent>

									{/* Manual Tab */}
									<TabsContent value="manual">
										<p className="mb-4">{translate('payfit.fileModal.improvingIntegration')}</p>
										<p className="mb-4">
											{translate('payfit.fileModal.currentProcedure')}{' '}
											<s>{translate('payfit.fileModal.tedious')}</s>{' '}
											{translate('payfit.fileModal.magic')}
										</p>

										<ol className="list-decimal pl-5 space-y-2">
											<li>
												{translate('payfit.fileModal.step1.download1')}
												<strong>{translate('payfit.fileModal.step1.download2')}</strong>
												{translate('payfit.fileModal.step1.download3')}
												<a
													href="/files/payfit_retribucion_especie_example.xlsx"
													download
													className="italic underline"
												>
													{translate('payfit.fileModal.step1.templateExample')}
												</a>
												:
												<ol className="list-decimal pl-5 mt-2 space-y-1">
													<li>{translate('payfit.fileModal.step1.1')}</li>
													<li>{translate('payfit.fileModal.step1.2')}</li>
													<li>
														{translate('payfit.fileModal.step1.3.1')}
														<strong>{translate('payfit.fileModal.step1.3.2')}</strong>
														{translate('payfit.fileModal.step1.3.3')}
														<a
															href="/files/payfit_retribucion_especie_example.xlsx"
															download
															className="italic underline"
														>
															{translate('payfit.fileModal.step1.templateLink')}
														</a>
														. {translate('payfit.fileModal.step1.doubts')}{' '}
														<a
															href="https://drive.google.com/file/d/17zT-ei9IXZWLCi802sQQqw7gv4Mut5qJ/view?usp=drive_link"
															target="_blank"
															rel="noopener noreferrer"
															className="italic underline inline-flex items-center gap-1"
														>
															{translate('payfit.fileModal.step1.videoTutorial')}
															<ExternalLink className="w-3 h-3" />
														</a>{' '}
														{translate('payfit.fileModal.step1.contactDirectly')}{' '}
														<strong>{ProjectConfig.ARCA_CONTACT.EMAIL}</strong>.
													</li>
												</ol>
											</li>
											<li>
												{translate('payfit.fileModal.step2.1')}
												<strong>{translate('payfit.fileModal.step2.2')}</strong>
												{translate('payfit.fileModal.step2.3')}
											</li>
											<li>{translate('payfit.fileModal.step3')}</li>
										</ol>

										<div className="mt-4">
											<div className="flex flex-wrap gap-2 mb-4">
												<Button asChild className="btn-arca-primary">
													<label className="cursor-pointer">
														{translate('payfit.fileModal.uploadFile')}
														<input
															type="file"
															accept=".xls,.xlsx"
															className="hidden"
															onChange={handleFileUpload}
														/>
													</label>
												</Button>
												<Button
													className="bg-emerald-400 hover:bg-emerald-500 text-foreground"
													onClick={handleAutoFill}
													disabled={!sheetData || !payfitFileData}
												>
													{translate('payfit.fileModal.autoFill')}
												</Button>
											</div>
										</div>

										{fileName && (
											<div className="mt-2 flex items-center gap-2">
												<strong>{translate('payfit.fileModal.importedFile')}:</strong> {fileName}
												<Button
													variant="outline"
													size="sm"
													className="ml-4 text-destructive border-destructive hover:bg-destructive/10"
													onClick={() =>
														window.confirm(translate('payfit.fileModal.confirmDelete')) &&
														handleCloseModal(false)
													}
												>
													<X className="w-4 h-4 mr-1" />
													{translate('payfit.fileModal.delete')}
												</Button>
											</div>
										)}

										<div className="mt-4">
											<div className="flex flex-wrap gap-2">
												{sheetData && (
													<Button
														className="bg-emerald-400 hover:bg-emerald-500 text-foreground"
														onClick={handleVisualize}
														disabled={!sheetData}
													>
														{translate('payfit.fileModal.visualize')}
													</Button>
												)}
												{magicFilled && (
													<Button
														variant="outline"
														onClick={() => {
															analytics.track('payfit_funnel', {
																step: 'file_download'
															});
															handleDownload();
														}}
													>
														{translate('payfit.fileModal.downloadMagicFile')}
													</Button>
												)}
											</div>
										</div>

										<div className="mt-4">
											{!sheetData && !payfitFileData && (
												<p>
													{translate('payfit.fileModal.fileReadError')}
													<strong> {ProjectConfig.ARCA_CONTACT.EMAIL}</strong>{' '}
													{translate('payfit.fileModal.persistingIssues')}
												</p>
											)}
										</div>

										{dataVisible && sheetData && sheetData.length > 0 && (
											<div className="mt-8 mb-16 overflow-auto max-h-[300px] p-2 border border-border">
												<Table>
													<TableHeader>
														<TableRow>
															{sheetData[0].map((cell: any, cellIndex: number) => (
																<TableHead key={cellIndex}>{cell}</TableHead>
															))}
														</TableRow>
													</TableHeader>
													<TableBody>
														{sheetData.slice(1).map((row, rowIndex) => (
															<TableRow
																key={rowIndex}
																className={rowIndex % 2 === 0 ? 'bg-muted/30' : ''}
															>
																{row.map((cell: any, cellIndex: number) => (
																	<TableCell key={cellIndex}>{cell}</TableCell>
																))}
															</TableRow>
														))}
													</TableBody>
												</Table>
											</div>
										)}
									</TabsContent>
								</Tabs>
							)}
						</>
					)}

					{/* Other Software */}
					{selectedSoftware === SoftwareHHRR.OTHER && (
						<div className="mt-4">
							<Input
								placeholder={translate('payfit.fileModal.specifySoftware')}
								value={otherSoftware}
								onChange={(e) => setOtherSoftware(e.target.value)}
							/>
							<Button
								className="btn-arca-primary mt-2"
								onClick={handleInformArca}
								disabled={otherSoftware.trim() === ''}
							>
								{translate('payfit.fileModal.informArca')}
							</Button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PayfitFileModalV2;
